// decision-ledger-status-text.ts — V2-1 `avatar autopilot status --decisions`
// (spec V2 §4.1 "Sổ quyết định", review 03a L9).
//
// Bảng mã QĐ / câu hỏi / lựa chọn (option id + nhãn) / nguồn / thời điểm; mỗi
// quyết định tự áp kèm câu đảo `@avatar redirect: …`; mục "worker ghi nhận"
// riêng (E13); câu đang chờ liệt kê option id + nhãn và lệnh `answer` THẬT
// (`--question <qid> --text <option id>`, autopilot-answer-directive-command.ts).
// Chuỗi đã qua redactor ở decision-ledger-projection.ts; ở đây chỉ dàn trang.
import { replaceControlChars } from "./clip-untrusted-text.js";
import type {
  DecisionLedger,
  DecisionLedgerEntry,
  PendingDecisionQuestion,
} from "./decision-ledger-projection.js";

/**
 * Một ô bảng: một dòng, không `|` (tránh vỡ cột), không ký tự điều khiển —
 * chữ từ worker có `\r`/ANSI/OSC 8 giả được dòng trên terminal (review 03b M2).
 */
function cell(text: string): string {
  return replaceControlChars(text.replace(/\s*\n\s*/g, " ")).replace(/\|/g, "/");
}

function choice(entry: DecisionLedgerEntry): string {
  return entry.optionId === undefined
    ? entry.optionLabel
    : `${entry.optionId} (${entry.optionLabel})`;
}

function entryLines(entries: DecisionLedgerEntry[], missionId: string): string[] {
  if (entries.length === 0) return ["(chưa có quyết định nào)"];
  const rows = entries.map((e) =>
    [e.code, e.question, choice(e), e.source, e.decidedAt ?? "—"].map(cell).join(" | "),
  );
  const autoNotes = entries
    .filter((e) => e.origin === "auto")
    .map(
      (e) =>
        `⚠ ${e.code} tự áp theo quyền ủy nhiệm G0 (${cell(e.source)}) — muốn đổi: đảo bằng \`@avatar redirect: …\` ` +
        `hoặc \`avatar autopilot redirect --mission ${missionId} --text "…"\`.`,
    );
  return [
    "QĐ | Câu hỏi | Lựa chọn | Nguồn | Thời điểm",
    ...rows,
    ...(autoNotes.length > 0 ? ["", ...autoNotes] : []),
  ];
}

function pendingLines(missionId: string, pending: PendingDecisionQuestion[]): string[] {
  if (pending.length === 0) return [];
  const lines = ["", "Câu đang chờ người trả lời:"];
  for (const q of pending) {
    const ticket = q.ticketId === undefined ? "" : ` (${q.ticketId})`;
    lines.push(`- ${cell(q.questionId)}${ticket}: ${cell(q.question)}`);
    for (const o of q.options) {
      const mark = o.id !== undefined && o.id === q.recommendedOptionId ? " (khuyến nghị)" : "";
      lines.push(`    ${o.id === undefined ? "" : `${o.id} — `}${cell(o.label)}${mark}`);
    }
    const byId = q.options.length > 0 && q.options.every((o) => o.id !== undefined);
    const value = byId ? "<option id>" : q.options.length > 0 ? "<nhãn>" : "<câu trả lời>";
    lines.push(
      `  → avatar autopilot answer --mission ${missionId} --question ${cell(q.questionId)} --text ${value}`,
    );
  }
  return lines;
}

export function renderDecisionLedgerText(ledger: DecisionLedger, missionId: string): string {
  const worker =
    ledger.workerRecorded.length === 0
      ? []
      : [
          "",
          "Worker ghi nhận (không phải quyết định auto/người):",
          ...ledger.workerRecorded.map(
            (w) =>
              `- ${cell(w.ticketId)}${w.attemptNo === undefined ? "" : ` (attempt ${w.attemptNo})`}: ${cell(w.text)}`,
          ),
        ];
  // Review 03b L4: journal hỏng/ghi dở ⇒ sổ có thể thiếu mục — nói ra, đừng im.
  const status = ledger.journalStatus;
  const journalWarning =
    status === undefined || status === "clean"
      ? []
      : [
          `⚠ journal ${status} — sổ chỉ gồm các event trước điểm hỏng; kiểm bằng \`avatar autopilot doctor --mission ${missionId}\`.`,
        ];
  return [
    `Sổ quyết định — mission ${missionId}`,
    ...journalWarning,
    ...entryLines(ledger.entries, missionId),
    ...worker,
    ...pendingLines(missionId, ledger.pending),
  ].join("\n");
}
